import React, { useEffect, useState } from 'react';
import {
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  IconButton,
  Popover,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import { ConfirmDialogModal } from '../ConfirmDialog/ConfirmDialog';
import { DEFAULT_COMMENT_MAX_LENGTH } from './constants';

type CommentPopoverProps = {
  open: boolean;
  anchorEl: HTMLElement | null;
  comment: string;
  onSave: (value: string) => void;
  onDelete: () => void;
  onClose: () => void;
  isReadOnly?: boolean;
  maxLength?: number;
};
export const CommentPopover = ({
  open,
  anchorEl,
  comment,
  onSave,
  onDelete,
  onClose,
  isReadOnly = false,
  maxLength = DEFAULT_COMMENT_MAX_LENGTH,
}: CommentPopoverProps) => {
  const [draft, setDraft] = useState(comment);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (open) {
      setDraft(comment);
      setIsDirty(false);
    }
  }, [open, comment]);

  const handleSave = () => {
    onSave(draft);
    onClose();
  };

  const handleDelete = () => {
    onDelete();
    onClose();
  };

  const handleOutsideClose = () => {
    if (isDirty && !isReadOnly) {
      ConfirmDialogModal({
        title: 'Внимание!',
        contentText:
          'Ваш комментарий еще не сохранен. Завершить редактирование без сохранения?',
        confirmText: 'Закрыть без сохранения',
        cancelText: 'Вернуться',
      }).then((shouldClose) => {
        if (shouldClose) {
          onClose();
        }
      });
    } else {
      onClose();
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={(_, reason) => {
        if (reason === 'backdropClick') {
          handleOutsideClose();
        } else {
          onClose();
        }
      }}
      anchorOrigin={{ vertical: 'center', horizontal: 'left' }}
      transformOrigin={{ vertical: 'center', horizontal: 'right' }}
      disableAutoFocus
      disableEnforceFocus
      elevation={8}
      PaperProps={{ sx: { width: 320 } }}
    >
      <Card>
        <CardHeader
          title={<Typography variant="body1">Комментарий</Typography>}
          action={
            <Tooltip title="Закрыть без сохранения">
              <IconButton size="small" onClick={onClose}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          }
        />
        <CardContent sx={{ py: 0 }}>
          <TextField
            autoFocus
            multiline
            rows={3}
            fullWidth
            value={draft}
            onChange={(e) => {
              const value = e.target.value.slice(0, maxLength);
              setDraft(value);
              setIsDirty(value !== comment);
            }}
            variant="outlined"
            size="small"
            inputProps={{ maxLength }}
            InputProps={{
              readOnly: isReadOnly,
            }}
            helperText={`${draft.length}/${maxLength}`}
            FormHelperTextProps={{
              sx: { textAlign: 'right', m: 0, mt: 0.5 },
            }}
            placeholder={`Введите комментарий (Не более ${maxLength} символов) ...`}
          />
        </CardContent>
        <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
          {!isReadOnly && comment && (
            <Tooltip title="Удалить">
              <IconButton
                size="small"
                sx={{ color: 'text.disabled' }}
                onClick={handleDelete}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {!isReadOnly && (
            <Button
              size="small"
              variant="contained"
              onClick={handleSave}
              disabled={!isDirty}
              sx={{ marginLeft: 'auto' }}
            >
              Сохранить
            </Button>
          )}
        </CardActions>
      </Card>
    </Popover>
  );
};
